import {
  GENERATION_SIZING_STUDY_MODEL_VERSION,
  GENERATION_SIZING_STUDY_SCHEMA_VERSION,
  type GenerationImpactScenarioSnapshot,
  type GenerationSizingStudyGranularityCurve,
  type GenerationSizingStudyOption,
  type GenerationSizingStudySnapshot,
  type GenerationSizingStudyUniformEnvelope,
} from '../../types/generationImpact';
import {
  buildGenerationAlternativesMatrix,
  DEFAULT_GENERATION_ALTERNATIVE_BLOCK_COUNTS,
  DEFAULT_GENERATION_ALTERNATIVE_SIZING_INCREMENT_MW,
  type GenerationAlternativeRow,
} from './generationAlternatives';
import {
  hashGenerationImpactPayload,
  requireValidGenerationImpactScenarioIntegrity,
} from './generationImpactScenarioIntegrity';
import { sizeUniformGenerationBlocks } from './sizeUniformGenerationBlocks';
import {
  selectGranularityLadderRows,
  sweepGenerationBlockGranularity,
} from './sweepGenerationBlockGranularity';

const SELECTION_CRITERION = 'lowest-iso-nameplate-passing-n-1-plus-reserve' as const;

/**
 * Freeze the N-1 overbuild curve alongside the sizing envelope. Null whenever
 * the uniform-fleet guards refuse (mixed fleets, shared contingency groups,
 * input gaps), which is the same condition that nulls `uniformSizing`.
 */
function granularityCurve(
  scenario: GenerationImpactScenarioSnapshot,
): GenerationSizingStudyGranularityCurve | null {
  const sweep = sweepGenerationBlockGranularity(scenario.input, scenario.result);
  if (sweep.status !== 'available') return null;
  const rows = selectGranularityLadderRows(sweep);
  if (rows.length === 0) return null;
  return {
    basis: sweep.basis,
    basisRequirementMW: sweep.basisRequirementMW,
    configuredBlockCount: sweep.configuredBlockCount,
    netDeliveryFactor: sweep.netDeliveryFactor,
    incrementMW: sweep.incrementMW,
    rows: rows.map((row) => ({
      blockCount: row.blockCount,
      onlineBlocksAfterContingency: row.onlineBlocksAfterContingency,
      requiredGrossMWPerBlock: row.requiredGrossMWPerBlock,
      requiredInstalledGrossMW: row.requiredInstalledGrossMW,
      installedGrossMW: row.installedGrossMW,
      incrementRoundingPenaltyMW: row.incrementRoundingPenaltyMW,
      firmAfterContingencyMW: row.firmAfterContingencyMW,
      requiredInstalledRelativeToFinest: row.requiredInstalledRelativeToFinest,
    })),
  };
}

function uniformEnvelope(
  scenario: GenerationImpactScenarioSnapshot,
): GenerationSizingStudyUniformEnvelope | null {
  const sizing = sizeUniformGenerationBlocks(scenario.input, scenario.result);
  if (sizing.status !== 'available') return null;
  return {
    blockCount: sizing.blockCount,
    onlineBlocksAfterContingency: sizing.onlineBlocksAfterContingency,
    configuredGrossMWPerBlock: sizing.configuredGrossMWPerBlock,
    netDeliveryFactor: sizing.netDeliveryFactor,
    requiredLoadGrossMWPerBlock: sizing.requiredLoadGrossMWPerBlock,
    requiredReserveGrossMWPerBlock: sizing.requiredReserveGrossMWPerBlock,
    suggestedLoadGrossMWPerBlock: sizing.suggestedLoadGrossMWPerBlock,
    suggestedReserveGrossMWPerBlock: sizing.suggestedReserveGrossMWPerBlock,
    configuredMeetsLoad: sizing.configuredMeetsLoad,
    configuredMeetsReserve: sizing.configuredMeetsReserve,
    incrementMW: sizing.incrementMW,
  };
}

interface ProjectOptionOverrides {
  result?: GenerationImpactScenarioSnapshot['result'];
  inputHash?: string;
  resultHash?: string;
}

function projectOption(
  row: GenerationAlternativeRow,
  overrides: ProjectOptionOverrides = {},
): GenerationSizingStudyOption {
  const result = overrides.result ?? row.result;
  const siteNetMWPerBlock = result.unitResults[0]?.netMWPerUnit ?? 0;
  return {
    id: row.id,
    label: row.label,
    basis: row.kind === 'current' ? 'configured' : 'reserve-rounded',
    blockCount: row.blockCount,
    onlineBlocksAfterContingency: row.blockCount - 1,
    isoGrossMWPerBlock: row.isoGrossMWPerUnit,
    isoGrossNameplateMW: row.totalIsoGrossMW,
    siteNetMWPerBlock,
    normalNetMW: result.capacity.normalNetMW,
    nMinusOneNetMW: result.capacity.nMinusOneNetMW,
    criticalLoadMW: result.capacity.criticalLoadMW,
    reserveRequiredMW: result.capacity.reserveRequiredMW,
    nMinusOneMarginMW: result.capacity.nMinusOneMarginMW,
    nMinusOneReserveMarginMW: result.capacity.nMinusOneReserveMarginMW,
    nMinusOneVerdict: result.capacity.nMinusOneVerdict,
    nMinusOneReserveVerdict: result.capacity.nMinusOneReserveVerdict,
    probabilityLoadServed: result.reliability.probabilityLoadServed,
    peakGasDthPerDay: result.gas.peakDthPerDay,
    generationAndCcsConsumptionGalPerDay:
      result.water.generationConsumptionGalPerDay + result.water.ccsConsumptionGalPerDay,
    campusConsumptionGalPerDay: result.water.totalConsumptionGalPerDay,
    capturedCo2TonnesPerYear: result.emissions.capturedCo2TonnesPerYear,
    residualCo2TonnesPerYear: result.emissions.residualCo2TonnesPerYear,
    footprintAcres: result.footprint.totalAcres,
    ccsEnabled: row.ccsEnabled,
    inputHash: overrides.inputHash ?? hashGenerationImpactPayload(row.input),
    resultHash: overrides.resultHash ?? hashGenerationImpactPayload(result),
  };
}

function lowestPassingNameplate(options: readonly GenerationSizingStudyOption[]): string | null {
  return [...options]
    .filter(option => option.nMinusOneReserveVerdict === 'pass')
    .sort((left, right) => (
      left.isoGrossNameplateMW - right.isoGrossNameplateMW
      || left.isoGrossMWPerBlock - right.isoGrossMWPerBlock
      || left.blockCount - right.blockCount
      || left.id.localeCompare(right.id)
    ))[0]?.id ?? null;
}

/**
 * Build the coordinate-free capacity study frozen into new Test Fit exhibits.
 * CCS mode is held to the promoted scenario; capture-on/off comparisons must be
 * explicitly applied and saved in the workbench before becoming report basis.
 */
export function buildGenerationSizingStudySnapshot(
  candidate: GenerationImpactScenarioSnapshot,
): GenerationSizingStudySnapshot {
  const scenario = requireValidGenerationImpactScenarioIntegrity(candidate);
  const candidateBlockCounts = [...DEFAULT_GENERATION_ALTERNATIVE_BLOCK_COUNTS];
  const incrementMW = DEFAULT_GENERATION_ALTERNATIVE_SIZING_INCREMENT_MW;
  const common = {
    schemaVersion: GENERATION_SIZING_STUDY_SCHEMA_VERSION,
    modelVersion: GENERATION_SIZING_STUDY_MODEL_VERSION,
    incrementMW,
    basis: {
      baselineInputHash: scenario.inputHash,
      baselineResultHash: scenario.resultHash,
      candidateBlockCounts,
      selectionCriterion: SELECTION_CRITERION,
    },
    uniformSizing: uniformEnvelope(scenario),
    contingencyGranularity: granularityCurve(scenario),
  } as const;
  const matrix = buildGenerationAlternativesMatrix(scenario.input, {
    candidateBlockCounts,
    ccsModes: [scenario.input.ccs.enabled],
    sizingIncrementMW: incrementMW,
  });
  if (matrix.status === 'invalid') {
    return {
      ...common,
      status: 'unavailable',
      reason: matrix.errors.join(' '),
      options: [],
      lowestNameplatePassingReserveOptionId: null,
      warnings: [
        'No capacity-only alternatives were inferred; resolve the recorded sizing-study reason before using an alternative duty.',
      ],
    };
  }

  const options = matrix.rows.map(row => row.kind === 'current'
    ? projectOption(row, {
        result: scenario.result,
        inputHash: scenario.inputHash,
        resultHash: scenario.resultHash,
      })
    : projectOption(row));
  return {
    ...common,
    status: 'available',
    reason: null,
    options,
    lowestNameplatePassingReserveOptionId: lowestPassingNameplate(options),
    warnings: [
      `Per-block duties are rounded upward to ${incrementMW} MW for planning comparison; they are not vendor-available rating commitments.`,
      'The lowest-nameplate marker is a capacity-only screen, not a preferred project configuration or reliability recommendation.',
      'Technology, derates, auxiliary fraction, heat rate, operating basis, water intensity, footprint intensity, and the promoted CCS mode are held constant.',
      'The contingency-granularity curve is a capacity screen only: it assumes identical blocks on independent contingency groups and does not price the switchgear, transformers or collector system that finer granularity requires, nor confirm that vendor ratings exist at the implied block size.',
      scenario.result.reliability.caveat,
    ],
  };
}
